import {
  forwardRef,
  FunctionComponent,
  MutableRefObject,
  PropsWithoutRef,
  Ref,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import styled, { css } from 'styled-components';

import { BaseProps, ForwardProps } from '../../types';
import { defaultThemeProp } from '../../theme';
import Popover, { Delay, PopoverProps } from '../Popover';
import { useConsolidatedRef, useUID } from '../../hooks';
import { calculateFontSize } from '../../styles';

export interface TooltipProps extends BaseProps {
  /** Text content for the tooltip. */
  children: string | string[];
  /** Element to serve as the tooltip's target. */
  target: Element | null;
  /**
   * Delay showing the tooltip on mouseenter.
   * @default "short"
   */
  showDelay?: Delay;
  /**
   * Delay hiding the tooltip on mouseleave.
   * @default "long"
   */
  hideDelay?: Delay;
  /**
   * If true, the Tooltip will render outside of the current DOM hierarchy in a [portal](https://reactjs.org/docs/portals.html).
   * @default false
   */
  portal?: PopoverProps['portal'];
  /**
   * If true, the Tooltip will automatically apply an aria-describedby attribute to its target.
   * @default true
   */
  describeTarget?: boolean;
  ref?: Ref<HTMLDivElement>;
}

export const StyledTooltip = styled.div(({ theme }) => {
  const fontSize = calculateFontSize(theme.base['font-size'], theme.base['font-scale']);

  return css`
    background-color: ${theme.components.tooltip['background-color']};
    color: ${theme.components.tooltip['foreground-color']};
    font-size: ${fontSize.xxs};
    max-width: 40ch;
    padding: ${theme.base.spacing};
    white-space: pre-line;
    word-break: break-word;
  `;
});

StyledTooltip.defaultProps = defaultThemeProp;

const Tooltip: FunctionComponent<TooltipProps & ForwardProps> = forwardRef(
  (
    {
      children,
      target,
      showDelay = 'short',
      hideDelay = 'long',
      portal = false,
      describeTarget = true,
      ...restProps
    }: PropsWithoutRef<TooltipProps>,
    ref: Ref<HTMLDivElement>
  ) => {
    const id = useUID();
    const [show, setShow] = useState(false);
    const tooltipRef = useConsolidatedRef(ref);
    const lastClickedRef: MutableRefObject<EventTarget | null> = useRef(null);

    const showTooltip = useCallback(() => {
      setShow(true);
    }, []);

    const onMouseDown = useCallback(
      ({ target: eTarget }: MouseEvent) => {
        lastClickedRef.current = eTarget;
        if (eTarget === tooltipRef.current || eTarget === target) return;
        setShow(false);
      },
      [target]
    );

    const onFocusOut = useCallback(() => {
      if (lastClickedRef.current !== tooltipRef.current) {
        setShow(false);
      }
      lastClickedRef.current = null;
    }, []);

    const onMouseEnter = useCallback(() => {
      setShow(true);
    }, [target]);

    const onMouseLeave = useCallback(() => {
      if (document.activeElement !== target) {
        setShow(false);
      }
    }, [target]);

    const onKeyDown = useCallback(
      ({ key }: KeyboardEvent) => {
        if (key === 'Escape' && document.activeElement === target) {
          setShow(false);
        }
      },
      [target]
    );

    useEffect(() => {
      if (!target) return;

      document.addEventListener('keydown', onKeyDown);
      document.addEventListener('mousedown', onMouseDown);

      target.addEventListener('focusin', showTooltip);
      target.addEventListener('focusout', onFocusOut);

      target.addEventListener('mouseenter', onMouseEnter);
      target.addEventListener('mouseleave', onMouseLeave);

      return () => {
        document.removeEventListener('keydown', onKeyDown);
        document.removeEventListener('mousedown', onMouseDown);

        target.removeEventListener('focusin', showTooltip);
        target.removeEventListener('focusout', onFocusOut);

        target.removeEventListener('mouseenter', onMouseEnter);
        target.removeEventListener('mouseleave', onMouseLeave);
      };
    }, [target, showTooltip, onMouseDown, onFocusOut, onMouseLeave, onKeyDown]);

    useEffect(() => {
      if (target && !target.getAttribute('aria-describedby') && describeTarget) {
        target.setAttribute('aria-describedby', id);
      }
    }, [describeTarget, target]);

    return (
      <Popover
        id={id}
        {...restProps}
        show={show}
        showDelay={showDelay}
        hideDelay={hideDelay}
        groupId='tooltip'
        strategy='fixed'
        portal={portal}
        as={StyledTooltip}
        role='tooltip'
        target={target}
        arrow
        placement='bottom'
        onMouseDown={onMouseDown}
        hideOnTargetHidden
        ref={tooltipRef}
      >
        {children}
      </Popover>
    );
  }
);

export default Tooltip;
